import React, { createContext, useEffect, useState } from 'react'
import { IMooring, IPosition, IPositionType } from 'src/generated-types'
import { IPositionDrawingWithFile } from 'src/generated-types/position-drawing'
import { ISelectedPositionTypes } from 'src/generated-types/position-request-params'

export const DEFAULT_POSITION_TYPE_FILTER: ISelectedPositionTypes = {
  mooringLines: false,
  corners: false,
  bridles: false,
  frameLines: false,
  cages: false,
  drawnGeometries: false,
  includeHidden: false,
  includeInactive: false,
}

interface MooringsContextProps {
  selectedPosition?: IPosition
  setSelectedPosition: (position?: IPosition) => void
  selectedMooring?: IMooring
  setSelectedMooring: (mooring?: IMooring) => void
  selectedMoorings: IMooring[]
  setSelectedMoorings: (moorings: IMooring[]) => void
  selectedPositions: IPosition[]
  setSelectedPositions: React.Dispatch<React.SetStateAction<IPosition[]>>
  positionTypeFilter: ISelectedPositionTypes
  setPositionTypeFilter: (positionTypeFilter: ISelectedPositionTypes) => void
  isCritical: boolean | null
  setIsCritical: (value: boolean | null) => void
  selectedPositionDrawing?: IPositionDrawingWithFile
  setSelectedPositionDrawing?: (positionDrawing: IPositionDrawingWithFile) => void
}

export const MooringsCtx = createContext<MooringsContextProps>({
  setSelectedPosition: () => {},
  setSelectedMooring: () => {},
  selectedMoorings: [],
  selectedPositions: [],
  positionTypeFilter: DEFAULT_POSITION_TYPE_FILTER,
  setPositionTypeFilter: () => {},
  setSelectedMoorings: () => {},
  setSelectedPositions: () => {},
  isCritical: null,
  setIsCritical: () => {},
})

export const MooringsContextProvider: React.FC = ({ children }) => {
  const [selectedPosition, setSelectedPosition] = useState<IPosition | undefined>()
  const [selectedMooring, setSelectedMooring] = useState<IMooring | undefined>()
  const [selectedMoorings, setSelectedMoorings] = useState<IMooring[]>([])
  const [selectedPositions, setSelectedPositions] = useState<IPosition[]>([])
  const [positionTypeFilter, setPositionTypeFilter] = useState<ISelectedPositionTypes>({
    ...DEFAULT_POSITION_TYPE_FILTER,
  })
  const [isCritical, setIsCritical] = useState<boolean | null>(null)
  const [selectedPositionDrawing, setSelectedPositionDrawing] = useState<IPositionDrawingWithFile>()

  useEffect(() => {
    selectedMoorings.forEach(mooring => {
      mooring.positions.forEach(position => {
        position.isTransparent = selectedPosition
          ? position.type !== IPositionType.Cage && position.id != selectedPosition?.id
          : false
      })
    })

    if (
      selectedPosition &&
      selectedMoorings.length > 0 &&
      selectedPosition.mooringId &&
      !selectedMoorings?.map(m => m.id).includes(selectedPosition.mooringId)
    ) {
      setSelectedPosition(undefined)
    }
  }, [selectedMoorings, selectedPosition])

  useEffect(() => {
    return () => {
      selectedMoorings.forEach(mooring => {
        mooring.positions.forEach(position => {
          position.isTransparent = false
        })
      })
    }
  }, [])

  const contextValue: MooringsContextProps = {
    selectedPosition: selectedPosition,
    selectedMooring: selectedMooring,
    selectedMoorings: selectedMoorings,
    selectedPositions: selectedPositions,
    positionTypeFilter: positionTypeFilter,
    setSelectedPosition: setSelectedPosition,
    setSelectedMooring: setSelectedMooring,
    setSelectedMoorings: setSelectedMoorings,
    setSelectedPositions: setSelectedPositions,
    setPositionTypeFilter: setPositionTypeFilter,
    isCritical: isCritical,
    setIsCritical: setIsCritical,
    selectedPositionDrawing: selectedPositionDrawing,
    setSelectedPositionDrawing: setSelectedPositionDrawing,
  }

  return <MooringsCtx.Provider value={contextValue}>{children}</MooringsCtx.Provider>
}
